import React from 'react';
import { CertInfo } from '../../entities/cert-info';
import styles from './consult-certificate.module.css';

type CertificateResultProps = {
	cert: CertInfo;
};

function toYmdHms(date: Date): string {
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');
	const hours = String(date.getUTCHours()).padStart(2, '0');
	const minutes = String(date.getUTCMinutes()).padStart(2, '0');
	const seconds = String(date.getUTCSeconds()).padStart(2, '0');
	return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
}

function formatCreationTimestamp(value: string | number): string {
	const seconds = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(seconds)) return String(value);
	return toYmdHms(new Date(seconds * 1000));
}

function formatIsoDate(value: string): string {
	const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
	const date = new Date(hasTimezone ? value : `${value}Z`);
	if (Number.isNaN(date.getTime())) return value;
	return toYmdHms(date);
}

const CERT_TYPE_LABELS: Record<string, string> = {
	natural: 'Persona natural',
	legal_representative: 'Representante legal',
	professional: 'Profesional',
	employee: 'Empleado',
	company: 'Empresa',
	public_official: 'Funcionario público',
};

const DESCRIPTION_LABELS: Record<string, string> = {
	revoked: 'Revocado',
	suspended: 'Suspendido',
	reactivated: 'Activo',
	expired: 'Expirado',
};

const STATUS_LABELS: Record<string, string> = {
	Active: 'Activo',
	Inactive: 'Inactivo',
	Pending: 'Pendiente',
};

const MODIFICATION_LABELS: Record<string, string> = {
	revoked: 'Fecha de revocación',
	suspended: 'Fecha de suspensión',
	reactivated: 'Fecha de reactivación',
	expired: 'Fecha de expiración',
};

type StatusTone = 'success' | 'warning' | 'danger';

function getStatusTone(
	status: string,
	description: string | undefined,
): StatusTone {
	const desc = description?.toLowerCase();
	if (desc === 'suspended') return 'warning';
	if (desc === 'reactivated') return 'success';
	if (desc === 'revoked' || desc === 'expired') return 'danger';
	if (status === 'Active') return 'success';
	return 'danger';
}

const TONE_CLASS: Record<StatusTone, string> = {
	success: styles.statusActive,
	warning: styles.statusSuspended,
	danger: styles.statusInactive,
};

function getStatusLabel(
	status: string,
	description: string | undefined,
): string {
	const desc = description?.toLowerCase();
	if (desc && DESCRIPTION_LABELS[desc]) return DESCRIPTION_LABELS[desc];
	return STATUS_LABELS[status] ?? status;
}

const CertificateResult: React.FC<CertificateResultProps> = ({ cert }) => {
	const description = cert.StatusDescription?.toLowerCase();
	const statusLabel = getStatusLabel(cert.CertificateStatus, description);
	const toneClass =
		TONE_CLASS[getStatusTone(cert.CertificateStatus, description)];
	const certTypeLabel = CERT_TYPE_LABELS[cert.CertType] ?? cert.CertType;
	const modificationLabel = description
		? (MODIFICATION_LABELS[description] ?? 'Fecha de modificación')
		: undefined;

	return (
		<div className={styles.resultCard}>
			<div className={`${styles.statusBadge} ${toneClass}`}>
				{statusLabel}
			</div>

			<div className={styles.detailGrid}>
				<span className={styles.detailLabel}>Número de serie:</span>
				<span className={styles.detailValue}>{cert.Serial}</span>

				<span className={styles.detailLabel}>Suscriptor:</span>
				<span className={styles.detailValue}>{cert.OwnerName}</span>

				<span className={styles.detailLabel}>Tipo de certificado:</span>
				<span className={styles.detailValue}>{certTypeLabel}</span>

				<span className={styles.detailLabel}>Fecha de emisión:</span>
				<span className={styles.detailValue}>
					{formatCreationTimestamp(cert.CreationTimestamp)}
				</span>

				<span className={styles.detailLabel}>Fecha de expiración:</span>
				<span className={styles.detailValue}>
					{formatIsoDate(cert.ExpirationDate)}
				</span>

				{modificationLabel && cert.ModificationDate && (
					<>
						<span className={styles.detailLabel}>
							{modificationLabel}:
						</span>
						<span className={styles.detailValue}>
							{formatIsoDate(cert.ModificationDate)}
						</span>
					</>
				)}
			</div>
		</div>
	);
};

export default CertificateResult;
